/**
 * SkillsRail — shared smart-tile horizontal rail.
 *
 * Used in the ChatShellPage empty state and any other skill-launch surface
 * that needs the same visual pattern as the HomeView SuggestedSkillsRail.
 *
 * Fetches tiles in priority order:
 *   1. /api/v1/chat/smart-tiles (AI-personalized, live PDO data)
 *   2. /api/v1/chat/skill-tiles (rule-based, mode-filtered)
 *   3. `staticTiles` prop (curated fallback, always available)
 *
 * The caller receives `onLaunch(prompt, skillId, signalLabel?)` — both prompt and
 * skillId are always populated; signalLabel is provided by CategoryTile sub-skills.
 */

import { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  Compass,
  Eye,
  FileText,
  GitBranch,
  Lightbulb,
  Loader2,
  Mail,
  MessageSquare,
  Package,
  PenLine,
  Search,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import { clsx } from 'clsx';
import { agentLabApi, type SkillTile, type TileUnion } from '@/services/api';
import { CategoryTileCard } from './CategoryTileCard';
import { SkillPickerPanel } from './SkillPickerPanel';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CATEGORY_ICON_BG: Record<string, string> = {
  insight:  'bg-purple-50  text-purple-500',
  creative: 'bg-teal-50    text-brand-teal',
  seo:      'bg-emerald-50 text-emerald-600',
  cro:      'bg-rose-50    text-rose-500',
  paid_ads: 'bg-amber-50   text-amber-500',
  display:  'bg-slate-100  text-slate-500',
};

const CATEGORY_LABEL: Record<string, string> = {
  insight:  'Insight',
  creative: 'Creative',
  seo:      'SEO',
  cro:      'CRO',
  paid_ads: 'Paid',
  display:  'Display',
};

const CATEGORY_LABEL_COLOR: Record<string, string> = {
  insight:  'text-purple-500',
  creative: 'text-brand-teal',
  seo:      'text-emerald-600',
  cro:      'text-rose-500',
  paid_ads: 'text-amber-500',
  display:  'text-slate-500',
};

const OUTPUT_LABELS: Record<string, string> = {
  artifact: 'artifact',
  analysis: 'analysis',
  list:     'list',
  brief:    'brief',
};

const ICON_MAP: Record<string, React.ElementType> = {
  MessageSquare,
  Lightbulb,
  GitBranch,
  FileText,
  Eye,
  Search,
  Zap,
  Users,
  Compass,
  Target,
  Mail,
  Package,
  PenLine,
  TrendingUp,
};

// ---------------------------------------------------------------------------
// Default static tiles (curated, no backend required)
// ---------------------------------------------------------------------------

export type StaticSkillTile = Omit<SkillTile, 'context' | 'color_scheme'>;

export const CHAT_STATIC_TILES: StaticSkillTile[] = [
  {
    skill_id: 'ask_persona',
    skill_name: 'Ask the Persona',
    pitch: 'Deep Q&A grounded in cognitive graph data — decision factors, buyer scenarios, and intent signals.',
    category: 'insight',
    icon: 'MessageSquare',
    trigger_prompt: 'I want to ask a persona a question. Which client and persona should we use?',
    estimated_output: 'analysis',
    estimated_seconds: 15,
  },
  {
    skill_id: 'suggest_briefs',
    skill_name: 'Suggest Creative Briefs',
    pitch: 'Generate ranked brief ideas grounded in buyer scenarios and PDO keyword intent.',
    category: 'creative',
    icon: 'Lightbulb',
    trigger_prompt: "Suggest 3–5 creative brief ideas for a campaign. Let's start — which client and persona?",
    estimated_output: 'list',
    estimated_seconds: 20,
  },
  {
    skill_id: 'scenario_explorer',
    skill_name: 'Decision Architecture',
    pitch: "Render the persona's causal decision graph: PDO concepts, edges, and buyer scenarios.",
    category: 'insight',
    icon: 'GitBranch',
    trigger_prompt: 'Show me the decision architecture for a persona. Which client should we explore?',
    estimated_output: 'artifact',
    estimated_seconds: 12,
  },
  {
    skill_id: 'creative_review',
    skill_name: 'Creative Review',
    pitch: "Review any ad, email, or landing page through the persona's cognitive and emotional lens.",
    category: 'creative',
    icon: 'Eye',
    trigger_prompt: "I want to review a creative asset through a persona's lens. Share the asset or URL and tell me which persona to use.",
    estimated_output: 'analysis',
    estimated_seconds: 20,
  },
  {
    skill_id: 'seo_keyword_strategy',
    skill_name: 'Keyword Strategy',
    pitch: 'Derive a persona-grounded keyword cluster from the Purchase Decision Ontology.',
    category: 'seo',
    icon: 'Search',
    trigger_prompt: 'Build a keyword strategy for a client using their PDO. Which client and persona should we start with?',
    estimated_output: 'list',
    estimated_seconds: 25,
  },
  {
    skill_id: 'draft_brief',
    skill_name: 'Draft a Brief',
    pitch: 'Generate a 3-section creative brief with PDO-enriched angles — fast, no Firestore write.',
    category: 'creative',
    icon: 'FileText',
    trigger_prompt: 'Draft a creative brief for a campaign. Which client and creative direction are we starting from?',
    estimated_output: 'brief',
    estimated_seconds: 30,
  },
];

// ---------------------------------------------------------------------------
// Analytics tile types — chat-analytics-ui Phase 3
// ---------------------------------------------------------------------------

const ANALYTICS_TILE_TYPES = new Set<string>([
  'performance_anomaly',
  'alignment_opportunity',
  'formula_intelligence',
  'fatigue_diagnosis',
]);

// ---------------------------------------------------------------------------
// SkillCard — matches SuggestedSkillCard visual pattern
// ---------------------------------------------------------------------------

interface SkillCardProps {
  tile: SkillTile | StaticSkillTile;
  onLaunch: (
    prompt: string,
    skillId: string,
    extra?: { contextBlocks?: { type: string; value: unknown }[] },
  ) => void;
  disabled?: boolean;
}

export function SkillCard({ tile, onLaunch, disabled }: SkillCardProps) {
  const IconComponent = ICON_MAP[tile.icon] ?? Zap;
  const catLabel = CATEGORY_LABEL[tile.category] ?? tile.category;
  const catLabelColor = CATEGORY_LABEL_COLOR[tile.category] ?? 'text-slate-500';
  const iconBg = CATEGORY_ICON_BG[tile.category] ?? 'bg-slate-100 text-slate-500';
  const outputLabel = OUTPUT_LABELS[tile.estimated_output] ?? tile.estimated_output;

  return (
    <button
      type="button"
      onClick={() => {
        // Analytics tiles route via context_blocks (chart_axis) instead of
        // the skill-launcher path so the analytics router auto-renders the
        // chart on the first turn.
        const tileMeta = (tile as Partial<SkillTile>).metadata;
        const chartAxis = tileMeta && (tileMeta as Record<string, unknown>).chart_axis;
        if (
          ANALYTICS_TILE_TYPES.has(tile.skill_id) &&
          chartAxis &&
          typeof chartAxis === 'object'
        ) {
          onLaunch(tile.trigger_prompt, tile.skill_id, {
            contextBlocks: [{ type: 'chart_axis', value: chartAxis }],
          });
        } else {
          onLaunch(tile.trigger_prompt, tile.skill_id);
        }
      }}
      disabled={disabled}
      className={clsx(
        'group relative flex flex-col text-left rounded-2xl border border-slate-200 bg-white',
        'px-5 py-5 transition-all duration-200 shrink-0 w-56',
        'hover:border-slate-300 hover:shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08)]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal/40',
        disabled && 'opacity-40 cursor-not-allowed pointer-events-none',
      )}
    >
      {/* Top row: icon circle + category label + time */}
      <div className="flex items-center gap-2.5 mb-3.5">
        <div className={clsx('flex items-center justify-center w-7 h-7 rounded-lg shrink-0', iconBg)}>
          <IconComponent size={14} />
        </div>
        <span className={clsx('text-[10px] font-bold uppercase tracking-widest', catLabelColor)}>
          {catLabel}
        </span>
        <span className="ml-auto text-[10px] text-slate-300 font-mono tabular-nums shrink-0">
          ~{tile.estimated_seconds}s
        </span>
      </div>

      {/* Skill name */}
      <p className="text-sm font-semibold text-slate-800 leading-snug mb-2 group-hover:text-brand-teal transition-colors duration-150">
        {tile.skill_name}
      </p>

      {/* Pitch */}
      <p className="text-[12px] text-slate-400 leading-relaxed line-clamp-2 flex-1">
        {tile.pitch}
      </p>

      {/* Footer: output type + arrow */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">
          {outputLabel}
        </span>
        <span
          className={clsx(
            'flex items-center justify-center w-5 h-5 rounded-full transition-all duration-200',
            'bg-slate-100 text-slate-400',
            'group-hover:bg-brand-teal group-hover:text-white group-hover:translate-x-0.5',
          )}
        >
          <ArrowRight size={10} />
        </span>
      </div>
    </button>
  );
}

export function SkillCardSkeleton() {
  return (
    <div className="shrink-0 w-56 rounded-2xl border border-slate-100 bg-white px-5 py-5 animate-pulse">
      <div className="flex items-center gap-2.5 mb-3.5">
        <div className="w-7 h-7 rounded-lg bg-slate-100" />
        <div className="h-2.5 w-14 rounded-full bg-slate-100" />
      </div>
      <div className="h-4 w-4/5 rounded bg-slate-100 mb-2" />
      <div className="space-y-1.5 mb-4">
        <div className="h-3 w-full rounded bg-slate-50" />
        <div className="h-3 w-3/4 rounded bg-slate-50" />
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <div className="h-2.5 w-10 rounded bg-slate-100" />
        <div className="w-5 h-5 rounded-full bg-slate-100" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SkillsRail — horizontal scroll rail with smart-tile fetching
// ---------------------------------------------------------------------------

type TileSource = 'smart' | 'rule' | 'static';

export interface SkillsRailProps {
  /** Client UID — enables personalized smart tiles. Without it, only static tiles show. */
  clientUid?: string;
  /** Persona ID — narrows smart/rule tiles to a specific persona. */
  personaId?: string;
  /**
   * Mode context passed to the skill-tiles backend so tiles are filtered to
   * the relevant skill set (explore | plan | create).
   */
  mode?: 'explore' | 'plan' | 'create';
  /** Static fallback tiles shown when the backend is unreachable or returns empty. */
  staticTiles?: StaticSkillTile[];
  /** Number of dynamic tiles to request from the backend. */
  limit?: number;
  /**
   * Called when the user clicks a direct tile. Receives prompt AND skill_id.
   * For analytics tile types (`performance_anomaly`, `alignment_opportunity`,
   * `formula_intelligence`, `fatigue_diagnosis`) the optional `extra` arg
   * carries `contextBlocks` with the tile's `chart_axis` so the analytics
   * router auto-renders the chart.
   */
  onLaunch: (
    prompt: string,
    skillId: string,
    signalLabel?: string,
    extra?: { contextBlocks?: { type: string; value: unknown }[] },
  ) => void;
  /** Disables all tiles while a chat message is streaming. */
  disabled?: boolean;
  /** Label shown in section header — defaults to "Creative Skills". */
  label?: string;
  /** Custom section header content (replaces default label + source badge). */
  headerSlot?: React.ReactNode;
}

export function SkillsRail({
  clientUid,
  personaId,
  mode = 'explore',
  staticTiles = CHAT_STATIC_TILES,
  limit = 6,
  onLaunch,
  disabled,
  label = 'Creative Skills',
  headerSlot,
}: SkillsRailProps) {
  const [tiles, setTiles] = useState<TileUnion[]>([]);
  const [tileSource, setTileSource] = useState<TileSource>('static');
  const [loading, setLoading] = useState(false);
  // Track which category tile (by skill_id) has its picker panel open
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!clientUid) {
      setTiles([]);
      setTileSource('static');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setOpenCategoryId(null);

    // Try smart tiles first, fall back to rule-based
    agentLabApi
      .getSmartTiles(clientUid, personaId, limit, mode)
      .then((res) => {
        if (cancelled) return;
        if (res.tiles?.length) {
          const isSmart = res.tiles.some(
            (t) => t.tile_type !== 'category' && (t as SkillTile).context?.source === 'smart',
          );
          setTiles(res.tiles);
          setTileSource(isSmart ? 'smart' : 'rule');
        } else {
          // Fall through to rule-based
          return agentLabApi.getSkillTiles(clientUid, personaId, limit, mode).then((r) => {
            if (cancelled) return;
            setTiles(r.tiles ?? []);
            setTileSource('rule');
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Silent fallback to rule-based, then static
        agentLabApi
          .getSkillTiles(clientUid, personaId, limit, mode)
          .then((r) => {
            if (!cancelled) {
              setTiles(r.tiles ?? []);
              setTileSource('rule');
            }
          })
          .catch(() => {
            if (!cancelled) setTiles([]);
          });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [clientUid, personaId, mode, limit]);

  const displayTiles: TileUnion[] = tiles.length > 0
    ? tiles
    : staticTiles.map((t) => ({ ...t, tile_type: 'direct' as const, context: { client_uid: '', persona_id: '' }, color_scheme: 'teal' }));
  const isDynamic = tiles.length > 0;

  const sourceBadge =
    tileSource === 'smart' ? { label: 'AI', title: 'Personalized by NeuroGraph agent' }
    : tileSource === 'rule'  ? { label: 'live', title: 'Contextual rule-based suggestions' }
    : null;

  // The category tile that is currently open (for SkillPickerPanel)
  const openCategoryTile = displayTiles.find(
    (t) => t.tile_type === 'category' && t.skill_id === openCategoryId,
  );

  function handleCategoryToggle(skillId: string) {
    setOpenCategoryId((prev) => (prev === skillId ? null : skillId));
  }

  function handleSubSkillLaunch(prompt: string, skillId: string, signalLabel: string) {
    setOpenCategoryId(null);
    onLaunch(prompt, skillId, signalLabel);
  }

  return (
    <div className="w-full">
      {/* Section header */}
      <div className="flex items-center gap-2 mb-4 px-1 flex-wrap">
        {headerSlot ?? (
          <>
            <Sparkles size={12} className="text-brand-teal shrink-0" />
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
              {label}
            </span>
            {isDynamic && personaId && (
              <span className="text-[10px] font-mono text-slate-300 bg-slate-50 border border-slate-100 rounded-full px-2 py-0.5">
                {personaId}
              </span>
            )}
            {!isDynamic && !clientUid && (
              <span className="text-[10px] text-slate-300">
                — select a client for personalised suggestions
              </span>
            )}
            {loading && clientUid && (
              <span className="flex items-center gap-1 text-[10px] text-slate-300 ml-1">
                <Loader2 size={10} className="animate-spin" />
                loading…
              </span>
            )}
            {!loading && sourceBadge && (
              <span
                title={sourceBadge.title}
                className={clsx(
                  'ml-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full',
                  tileSource === 'smart'
                    ? 'bg-purple-50 text-purple-400 border border-purple-100'
                    : 'bg-slate-50 text-slate-300 border border-slate-100',
                )}
              >
                {sourceBadge.label}
              </span>
            )}
          </>
        )}
      </div>

      {/* Horizontal scroll rail */}
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-2 scroll-smooth"
        style={{ scrollbarWidth: 'none' }}
      >
        {loading
          ? Array.from({ length: Math.min(limit, 4) }).map((_, i) => (
              <SkillCardSkeleton key={i} />
            ))
          : displayTiles.map((tile, idx) => {
              if (tile.tile_type === 'category') {
                return (
                  <CategoryTileCard
                    key={`${tile.skill_id}-${idx}`}
                    tile={tile}
                    isOpen={openCategoryId === tile.skill_id}
                    onToggle={() => handleCategoryToggle(tile.skill_id)}
                    disabled={disabled}
                  />
                );
              }
              return (
                <SkillCard
                  key={`${tile.skill_id}-${idx}`}
                  tile={tile as SkillTile}
                  onLaunch={(prompt, skillId, extra) =>
                    onLaunch(prompt, skillId, undefined, extra)
                  }
                  disabled={disabled}
                />
              );
            })}

        {/* Trailing spacer so last card doesn't clip */}
        <div className="shrink-0 w-1" aria-hidden />
      </div>

      {/* SkillPickerPanel — rendered below the scroll rail, full width */}
      {openCategoryTile && openCategoryTile.tile_type === 'category' && (
        <SkillPickerPanel
          tile={openCategoryTile}
          isOpen={true}
          onLaunch={handleSubSkillLaunch}
          onClose={() => setOpenCategoryId(null)}
        />
      )}
    </div>
  );
}
